import CardWrapper from "components/commons/cardWrapper/CardWrapper";
import { strings } from "intl/fa";
import infoStyles from "./SponsoredInfo.module.scss";
import Footer from "components/commons/footer/Footer";
import {
  Divider,
  DividerThickness,
  NewButton,
  NewButtonSize,
  NewTextField,
  TextField,
} from "@bale/ui";
import Modal from "components/commons/modal/Modal";
import {
  GeneralErrors,
  GeneralValidators,
  useInputHandler,
} from "hooks/useInputHandler";
import { useAppDispatch, useAppSelector } from "utils/store";
import {
  digitsToHindi,
  formatStrings,
  fromMoneyFormats,
  toMoneyFormat,
} from "@bale/utils";
import { useConfigs } from "hooks/useConfigs";
import styles from "./SponsoredDetails.module.scss";
import { TagsDropdown } from "components/content/dialogAd/steps/publishStep/TagsDropdown";
import { ApiCategory, ApiTag } from "core/configs/configs.entity";
import { TermsAndConditions } from "components/content/dialogAd/steps/publishStep/PublishStep";
import { useCallback, useMemo, useState } from "react";
import { OrderAdActions, OrderAdPages } from "core/orderAd/orderAd.slice";
import { WebLogger } from "utils/logger";

enum HelperModals {
  MembersCountHelper = "MembersCountHelper",
  TagsHelper = "TagsHelper",
  PriceHelper = "PriceHelper",
}

const modalMaker = (modal: HelperModals) => {
  if (modal === HelperModals.MembersCountHelper) {
    return {
      title: strings.sponsored_messages.channelMembersTitle,
      body: strings.sponsored_messages.channelMembersModalText,
    };
  }
  if (modal === HelperModals.TagsHelper) {
    return {
      title: strings.sponsored_messages.tagsTitle,
      body: strings.sponsored_messages.tagsModalText,
    };
  }
  if (modal === HelperModals.PriceHelper) {
    return {
      title: strings.sponsored_messages.priceTitle,
      body: "strings.sponsored_messages.priceModalText",
    };
  }
};
export const SponsoredDetails = () => {
  const dispatch = useAppDispatch();

  const { view_count } = useAppSelector((state) => state.orderAd.info);
  const { categories, max_sponsored_message_view, min_sponsored_message_view } =
    useConfigs();

  const closeHelperModal = () => {
    setShowingHelperModal(false);
  };

  const viewCountInputHandler = useInputHandler({
    rules: [
      GeneralValidators.minAmount(min_sponsored_message_view),
      GeneralValidators.maxAmount(max_sponsored_message_view),
    ],
    errorMessages: {
      [GeneralErrors.MinimumAmount]: formatStrings(
        strings.sponsored_messages.min_sponsored_view_order,
        {
          min: digitsToHindi(toMoneyFormat(String(min_sponsored_message_view))),
        }
      ),
      [GeneralErrors.MaximumAmount]: formatStrings(
        strings.sponsored_messages.max_sponsored_view_order,
        {
          max: digitsToHindi(toMoneyFormat(String(max_sponsored_message_view))),
        }
      ),
    },
    numberOnly: true,
    beforeChange: fromMoneyFormats,
    formatter: toMoneyFormat,
    initialValue: view_count ? String(view_count) : undefined,
  });

  const [selectedTag, setSelectedTag] = useState<ApiTag | undefined>(undefined);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [subcategories, setSubcategories] = useState<ApiCategory[] | null>(
    null
  );
  const [selectedSubcategory, setSelectedSubcategory] =
    useState<ApiCategory | null>(null);
  const [showingHelperModal, setShowingHelperModal] = useState<
    false | HelperModals
  >(false);

  const needsSubcategory = useMemo<boolean>(() => {
    return Boolean(selectedTag?.Subcategory);
  }, [categories, selectedTag]);

  const onCategoryChange = useCallback((tag: ApiTag) => {
    setSelectedTag(tag);
    setSubcategories(tag.Subcategory);
    setSelectedSubcategory(null);
  }, []);

  const onSubcategoryChange = useCallback((subcategory: ApiCategory) => {
    setSelectedSubcategory(subcategory);
  }, []);

  const handleChangeTerms = useCallback(() => {
    setTermsAccepted((prevState: boolean) => !prevState);
  }, []);

  function onSubmit() {
    dispatch(
      OrderAdActions.setOrderAdInfo({
        tag_1: selectedTag?.Id,
        tag_2: selectedSubcategory?.Id,
        view_count: +viewCountInputHandler.rawValue,
      })
    );
    dispatch(OrderAdActions.setPage(OrderAdPages.SponsoredFinalStep));
  }

  const modalProperties = {
    ...modalMaker(showingHelperModal || HelperModals.MembersCountHelper),
  };

  const isDisabled = (function () {
    if (!viewCountInputHandler.rawValue) return true;
    if (viewCountInputHandler.error) return true;
    if (!selectedTag) return true;
    if (needsSubcategory && !selectedSubcategory) return true;
    if (!termsAccepted) return true;
    return false;
  })();

  return (
    <>
      <div className={infoStyles.Content}>
        <CardWrapper className={infoStyles.Card}>
          <div className={styles.SectionHeader}>
            <p className={styles.SectionTitle}>
              {strings.sponsored_messages.view_count}
            </p>
          </div>
          <NewTextField
            {...viewCountInputHandler}
            helpText={
              viewCountInputHandler.error
                ? undefined
                : formatStrings(
                    strings.sponsored_messages.min_sponsored_view_order,
                    {
                      min: digitsToHindi(
                        toMoneyFormat(String(min_sponsored_message_view))
                      ),
                    }
                  )
            }
            placeholder={strings.sponsored_messages.view_count_placeholder}
            label={strings.sponsored_messages.view_count}
            className={styles.ViewInput}
            direction={viewCountInputHandler.rawValue ? "ltr" : "rtl"}
            data-testid="view-count-input"
          />

          <p className={styles.TagsDescription}>
            {formatStrings(
              strings.dialog_ad_form.more_settings_step.tags.description,
              {
                value: strings.ad,
              }
            )}
          </p>

          <TagsDropdown
            onCategoryChange={onCategoryChange}
            onSubcategoryChange={onSubcategoryChange}
            categories={categories}
            subcategories={subcategories}
          />
          <TermsAndConditions
            checked={termsAccepted}
            onChange={handleChangeTerms}
          />

          {!!showingHelperModal && (
            <Modal
              title={modalProperties.title!}
              primaryButton={{
                onClick: closeHelperModal,
                label: strings.common.button.understood,
              }}
              content={modalProperties.body}
              onClose={() => closeHelperModal()}
            />
          )}
        </CardWrapper>
      </div>
      <Footer>
        <NewButton
          disabled={isDisabled}
          onClick={onSubmit}
          className={infoStyles.Button}
          size={NewButtonSize.LARGE}
          data-testid="confirm-button"
        >
          {strings.common.button.submit_and_continue}
        </NewButton>
      </Footer>
    </>
  );
};
